/**
 * Royal Road wire types: what the server sends for a player's road on a
 * slot, a Lamp Run and the Hi-Lo bonus. Every amount is in coins (already
 * multiplied by the slot's fixed bet). Checked against real responses in
 * `fixtures/road/`, exported by the server's RoadFixturesExportTest.
 */
import type { TrailBonus, TrailGame, Wallet } from './index';

export { RUN_NAME, LUCKY_RUN_NAME } from './runName';

/** Road slots this build can draw: it has their art and words. A slot shows
 *  its road only where the server's list for the player
 *  (`Player.featureThemes.royal_road`) has it too, so a world listed here
 *  stays dark until the server switches it on. In chapter order. */
export const ROAD_THEMES: readonly string[] = [
  'forest',
  'royal',
  'underwater',
  'frost',
  'frontier',
  'neon',
  'caravan',
  'sherbet_square',
];

/** The slots with a road for a server that sends no per-player list (one
 *  from before `featureThemes`). */
export const ROAD_THEMES_DEFAULT: readonly string[] = ['forest', 'royal'];

/** Room a road slot needs below its reels for the SPIN row and HUD, shared
 *  by the engine's layout and the app's panel layout. */
export const ROAD_SPIN_AREA = 150;

/** A Lamp Run ring cell. Lamps stay dark until friends light them (Phase 2). */
export type RingCellKind =
  | 'coins_small'
  | 'coins_big'
  | 'free_spins'
  | 'silver'
  | 'bomb'
  | 'lamp1'
  | 'lamp2'
  | 'lamp3'
  | 'lamp4';

/** A trail square the token reached on a spin, and what it paid. */
export interface RoadStep {
  position: number;
  /** The square's label: `fs:3`, `coins:50`, `gems:1`, `gift` or `run`. */
  square: string;
  coins: number;
  gems: number;
  freeSpins: number;
}

/** The trail under the reels. Squares read `start`, `run`, `gift`, `fs:N`,
 *  `gems:N` or `coins:N` (N in coins). Level 0 is the short first lap. The
 *  gift square sits mid-lap from level 1, on every road, once the Wheel Run
 *  is on anywhere for the player. */
export interface RoadTrail {
  level: number;
  position: number;
  meter: number;
  cost: number;
  collectorSymbolId: string;
  squares: string[];
  /** Collector symbols counted on this spin. */
  collected: number;
  steps: RoadStep[];
}

/** A feature-panel lamp: one per kind of ring prize. */
export interface RoadLamp {
  id: RingCellKind;
  lit: boolean;
  /** What a landing pays, for lit lamps ("38", "5 free"). */
  label?: string;
}

/** About how many paid spins until the next Lamp Run, by the sooner route. */
export interface RoadRunIn {
  route: 'trail' | 'ready';
  spins: number;
}

/** A player's Royal Road on one slot. */
export interface RoadState {
  rules: 2;
  /** The slot's bet B; a spin can stake B times 1, 2, 4 or 8. */
  bet: number;
  trail: RoadTrail;
  /** Side lights lit, towards `readyMax`. A full set starts a Lamp Run. */
  ready: number;
  readyMax: number;
  readyProgress: number;
  spinsPerLight: number;
  silverPot: number;
  goldPot: number;
  goldOpen: boolean;
  lamps: RoadLamp[];
  runs: number;
  runIn: RoadRunIn;
  /** The story on this slot (Phase 3); absent without `road_story`. */
  story?: StoryState;
  /** The gift square's bonus while it waits to be played, in the Sweet
   *  Trail's shape: the boxes, or with `game` set the game to open with
   *  `POST .../trail/game`. The token waits on the gift square until it's
   *  played; shown whenever it's here, even once the square has gone. */
  gift?: TrailBonus | null;
  /** The gift square's game while it's offered or open, to open again after
   *  a reload. Only on `GET road`. */
  giftGame?: TrailGame | null;
}

/** The Wheel Run on a slot without a road of its own (`wheel_run`): its
 *  lamps light with the slot's stars. Absent on the eight roads. */
export interface RoadState {
  /** The slot's stars, 0 to 3. */
  stars?: number;
  /** Coins bet on paid spins on this slot, as the server counts them (since
   *  20 October 2026): what the stars come from. */
  wagered?: number;
  /** The `wagered` at which the next star comes; absent with all three. */
  nextStarAt?: number;
}

export interface BoardRunCell {
  cell: number;
  kind: RingCellKind;
  /** Coins for coin cells and the silver pot; free spins for free_spins. */
  value?: number;
  lit: boolean;
}

export interface RunPot {
  coins: number;
  gems: number;
  freeSpins: number;
}

/** A Lamp Run. Where each STOP lands is decided by the server and never sent. */
export interface BoardRunState {
  runId: string;
  origin: 'trail' | 'ready';
  /** The first run on a slot: fewer stops, no bombs, a big first prize. */
  lucky: boolean;
  ring: BoardRunCell[];
  lives: number;
  livesMax: number;
  stops: number;
  stopsMax: number;
  pot: RunPot;
  status: 'open' | 'done' | 'expired';
}

export interface BoardStopRequest {
  runId: string;
  stop: number;
}

export interface BoardStopResult {
  stop: number;
  cell: number;
  kind: RingCellKind;
  prize: { coins: number; freeSpins: number };
  lives: number;
  stops: number;
  pot: RunPot;
  over: boolean;
}

/** The Hi-Lo bonus offered after a collected run (Gamble on only). */
export interface HiLoOffer {
  playId: string;
  kind: 'hilo';
  base: number;
  maxValue: number;
}

export interface BoardCollectResponse {
  credited: RunPot;
  wallet: Wallet;
  road: RoadState;
  bonus: HiLoOffer | null;
}

export interface HiLoState {
  card: number;
  rung: number;
  value: number;
  /** The base: a wrong guess still pays this. */
  floor: number;
  oddsHigher: number;
  oddsLower: number;
}

export interface PlayActRequest {
  sessionId: string;
  step: number;
  action: 'start' | 'guess' | 'take';
  arg?: 'higher' | 'lower';
}

export interface PlayActResult {
  step: number;
  reveal: { card: number | null };
  state: HiLoState;
  over: boolean;
  payout: number | null;
}

export interface PlayView {
  playId: string;
  kind: 'hilo';
  status: 'offered' | 'open' | 'done' | 'expired' | 'declined';
  step: number;
  base: number;
  maxValue: number;
  state: HiLoState | null;
  payout: number | null;
}

/** 409 bodies: a run waiting (`BOARD_RUN_OPEN`) or out of turn (`RUN_CONFLICT`). */
export interface BoardRunConflict {
  error: 'BOARD_RUN_OPEN' | 'RUN_CONFLICT';
  boardRun: BoardRunState;
}

export interface PlayConflict {
  error: 'PLAY_CONFLICT';
  play: PlayView;
}

// Phase 2a: restoring the road's landmarks with coins.

/** A landmark on a slot's road. The first is where the road starts: it is
 *  always restored and costs nothing. */
export interface RoadLandmark {
  /** Which landmark (`fox_den`, `owls_tree`, ...): the client names and
   *  paints it. */
  id: string;
  /** Coins to restore it. */
  cost: number;
  restored: boolean;
}

/** The road's landmarks ride on the road state (spin, `GET road`, collect). */
export interface RoadState {
  /** Landmarks restored past the start, 0 to 4. */
  roadStep?: number;
  /** The road's chapter in the story: Forest 1, Royal Reels 2, then the
   *  first slot of each other world, 3 to 8. */
  chapter?: number;
  landmarks?: RoadLandmark[];
}

/** `POST /sessions/{id}/road/restore`. `landmark` must be the next one
 *  (`roadStep + 1`). */
export interface RoadRestoreRequest {
  themeId: string;
  landmark: number;
}

export interface RoadRestoreResponse {
  landmark: number;
  id: string;
  cost: number;
  /** Coins taken: the cost, or 0 when it was already restored. */
  charged: number;
  wallet: Wallet;
  road: RoadState;
  /** This restore finished the chapter (the last landmark). */
  chapterComplete: boolean;
  /** The slot finishing the chapter opened for free (a world-map node id),
   *  when it did. */
  unlockedSlotId?: string;
}

/** The slot each road's chapter opens when it's done: its world-map node id
 *  and its theme. Mirrors the server's RoadCatalog. */
export const ROAD_NEXT_SLOT: Readonly<Record<string, { node: number; theme: string }>> = {
  forest: { node: 2, theme: 'royal' },
  royal: { node: 3, theme: 'gardens' },
  // Each other world's chapter opens that world's second slot.
  underwater: { node: 9, theme: 'map_fragment' },
  frost: { node: 16, theme: 'avalanche' },
  frontier: { node: 23, theme: 'gold_mine' },
  neon: { node: 30, theme: 'arcade' },
  caravan: { node: 37, theme: 'pharaoh_tomb' },
  sherbet_square: { node: 44, theme: 'lollipop_lane' },
};

/** 402 body: the player can't afford it. */
export interface RoadRestoreShort {
  error: 'NOT_ENOUGH_COINS';
  cost: number;
  coins: number;
}

// Phase 2b
//
// The road's mini-games, played through the same `/v1/plays/*` endpoints as
// Hi-Lo. Each kind deals its outcome on the server when it's offered and
// reveals one act at a time; only what was revealed ever reaches the client.

/** Every game the app can play; the server's list (fixtures `catalog.games`)
 *  must be inside it. */
export const PLAY_KINDS = ['hilo', 'pick', 'smash', 'match', 'scratch', 'wheel', 'cups', 'dig', 'whack'] as const;

/** A game the server can offer. */
export type PlayKind = (typeof PLAY_KINDS)[number];

/** Where a play pays: the wallet, or (a lamp's game during a Lamp Run) the
 *  run's pot, which the run's collect pays. */
export type PlayCreditTo = 'wallet' | 'parent';

/** What a play paid when it ended. Free spins are at the slot's bet B. */
export interface PlayPrize {
  coins: number;
  gems: number;
  freeSpins: number;
  /** The coins are the slot's gold pot (which starts again). */
  gold?: boolean;
  /** The play's floor raised what the game itself paid: a visitor's game
   *  pays at least half the bet, so these coins are a thank-you. */
  floored?: boolean;
}

/** A play on offer: enough to open its game. */
export interface PlayOffer<K extends PlayKind = PlayKind, S = unknown> {
  playId: string;
  kind: K;
  /** The unit its table is written in (Hi-Lo: the floor it always pays). */
  base: number;
  /** The most it can pay, in coins. */
  maxValue: number;
  /** trail, ready, run_bonus, lamp, visitor, first_play, ... */
  origin?: string;
  creditTo?: PlayCreditTo;
  /** What the game shows before the first act. */
  setup?: S;
}

/** A play as the server sees it now (`GET /v1/plays/{id}`). */
export interface AnyPlayView<K extends PlayKind = PlayKind, S = unknown, St = unknown> {
  playId: string;
  kind: K;
  status: 'offered' | 'open' | 'done' | 'expired' | 'declined';
  step: number;
  base: number;
  maxValue: number;
  /** The state after the last act; null before the first. */
  state: St | null;
  payout: number | null;
  origin?: string;
  creditTo?: PlayCreditTo;
  setup?: S;
  prize?: PlayPrize;
}

/** One act in any game. `action` and `arg` are the game's own. */
export interface AnyPlayActRequest {
  sessionId: string;
  step: number;
  action: string;
  arg?: string;
}

/** The answer to one act. `payout` and `prize` are set when it ended the
 *  play; `pot` is the run's pot after a lamp's game paid into it. */
export interface AnyPlayActResult<R = unknown, St = unknown> {
  step: number;
  reveal: R;
  state: St;
  over: boolean;
  payout: number | null;
  prize?: PlayPrize;
  pot?: RunPot;
}

/** Pick a prize: `setup.boxes` wrapped boxes; act `pick` with the box's
 *  index opens it and ends the play. Only the opened box is ever sent. */
export interface PickSetup {
  boxes: number;
}

export interface PickOpened {
  box: number;
  coins: number;
}

export type PickOffer = PlayOffer<'pick', PickSetup>;
export type PickPlayView = AnyPlayView<'pick', PickSetup, PickOpened>;
export type PickActResult = AnyPlayActResult<PickOpened, PickOpened>;

/** Smash: `objects` hide coins, a doubler (×2 on the lot) or nothing; act
 *  `smash` with an object's index, `smashes` times. Only smashed objects
 *  are ever sent. */
export interface SmashSetup {
  objects: number;
  smashes: number;
}

export interface SmashSmashed {
  index: number;
  kind: 'coins' | 'double' | 'empty';
  coins: number;
}

export interface SmashState {
  smashed: SmashSmashed[];
  coins: number;
  doubles: number;
  /** What it pays now: the coins, doubled per doubler. */
  total: number;
  left: number;
}

export type SmashOffer = PlayOffer<'smash', SmashSetup>;
export type SmashPlayView = AnyPlayView<'smash', SmashSetup, SmashState>;
export type SmashActResult = AnyPlayActResult<SmashSmashed, SmashState>;

/** Find the pairs: `cards` face down; act `flip` with a card's index turns
 *  it, two make a try. `goldPair` is what the gold pair pays (the gold pot
 *  when `goldPot`). Faces are `p0`..`p4` and `gold`. */
export interface MatchSetup {
  cards: number;
  tries: number;
  goldPair: number;
  goldPot: boolean;
}

export interface MatchTurned {
  index: number;
  face: string;
  /** What its pair pays. */
  coins: number;
}

export interface MatchState {
  /** Every card turned so far. */
  seen: MatchTurned[];
  /** The cards of the pairs found. */
  matched: number[];
  /** A card turned, waiting for its partner. */
  open: number | null;
  triesLeft: number;
  total: number;
  gold: boolean;
}

export type MatchOffer = PlayOffer<'match', MatchSetup>;
export type MatchPlayView = AnyPlayView<'match', MatchSetup, MatchState>;
export type MatchActResult = AnyPlayActResult<MatchTurned, MatchState>;

/** Scratch card: `tiles` tiles, each one of the pictures (0 to
 *  values.length - 1), each picture worth `values[picture]` coins. Act
 *  `scratch` with a tile's index, or `all`. Three of a picture win it. */
export interface ScratchSetup {
  tiles: number;
  values: number[];
}

export interface ScratchTile {
  index: number;
  picture: number;
}

export interface ScratchState {
  /** Uncovered tiles; every tile once the card has ended. */
  uncovered: ScratchTile[];
  /** The picture found three times, or null. */
  won: number | null;
  prize: number;
}

export type ScratchOffer = PlayOffer<'scratch', ScratchSetup>;
export type ScratchPlayView = AnyPlayView<'scratch', ScratchSetup, ScratchState>;
/** A scratch reveals the tiles it uncovered. */
export type ScratchActResult = AnyPlayActResult<ScratchTile[], ScratchState>;

/** The wheel: its segments in order round it; act `spin` stops it. */
export interface WheelSegment {
  kind: 'coins' | 'fs' | 'gold';
  coins: number;
  freeSpins: number;
  /** The gold segment is the slot's gold pot. */
  goldPot: boolean;
}

export interface WheelSetup {
  segments: WheelSegment[];
}

export interface WheelLanding extends WheelSegment {
  segment: number;
}

export type WheelOffer = PlayOffer<'wheel', WheelSetup>;
export type WheelPlayView = AnyPlayView<'wheel', WheelSetup, WheelLanding>;
export type WheelActResult = AnyPlayActResult<WheelLanding, WheelLanding>;

/** Find the coins: `cups` cups. Act `shuffle` reveals where the coins
 *  start and each swap of two positions, in order; then act `cup` with a
 *  position (0 is the left) lifts it: `find` coins for the coins, `miss`
 *  otherwise. Nothing says where they are before the shuffle. */
export interface CupsSetup {
  cups: number;
  find: number;
  miss: number;
}

export interface CupsSwaps {
  start: number;
  swaps: [number, number][];
}

export interface CupsLifted {
  cup: number;
  /** Where the coins were. */
  prizeAt: number;
  found: boolean;
  coins: number;
}

export interface CupsState {
  shuffled: boolean;
  lifted: CupsLifted | null;
}

export type CupsOffer = PlayOffer<'cups', CupsSetup>;
export type CupsPlayView = AnyPlayView<'cups', CupsSetup, CupsState>;
export type CupsActResult = AnyPlayActResult<CupsSwaps | CupsLifted, CupsState>;

/** Dig for coins: `cells` squares, `bombs` of them bombs. Act `dig` with a
 *  square's index uncovers it and adds its coins to the pot; `bank` (after
 *  a dig) pays the pot. A bomb ends it and pays `perDig` for each safe dig
 *  instead. Only dug squares are ever sent. */
export interface DigSetup {
  cells: number;
  bombs: number;
  perDig: number;
}

export interface DigDug {
  index: number;
  kind: 'coins' | 'bomb';
  coins: number;
}

export interface DigState {
  dug: DigDug[];
  pot: number;
  /** How it ended, or null while digging. */
  ended: 'banked' | 'bomb' | 'cleared' | null;
}

export type DigOffer = PlayOffer<'dig', DigSetup>;
export type DigPlayView = AnyPlayView<'dig', DigSetup, DigState>;
/** A bank reveals nothing new (null). */
export type DigActResult = AnyPlayActResult<DigDug | null, DigState>;

/** Whack a mole, a speed round: `pops` moles over `holes` holes. Act
 *  `start` reveals the schedule (and the server notes the time); act
 *  `finish` with the taps as JSON (`WhackTap[]`, ms from the start) once
 *  the round has run `minMs`. The server counts the hits, and `bands[i]`
 *  hits or more pays `pays[i]` coins. */
export interface WhackSetup {
  holes: number;
  pops: number;
  minMs: number;
  bands: number[];
  pays: number[];
}

/** One mole: its hole, when it comes up and for how long (ms from the start). */
export interface WhackPop {
  hole: number;
  atMs: number;
  upMs: number;
}

export interface WhackTap {
  pop: number;
  tMs: number;
}

export interface WhackState {
  startedAt: number;
  hits: number | null;
  prize: number | null;
}

export type WhackOffer = PlayOffer<'whack', WhackSetup>;
export type WhackPlayView = AnyPlayView<'whack', WhackSetup, WhackState>;
/** `start` reveals the schedule; `finish` the hits the server counted. */
export type WhackActResult = AnyPlayActResult<WhackPop[] | number, WhackState>;

/** Each game's offer, by kind. */
export interface PlayOfferByKind {
  hilo: PlayOffer<'hilo'>;
  pick: PickOffer;
  smash: SmashOffer;
  match: MatchOffer;
  scratch: ScratchOffer;
  wheel: WheelOffer;
  cups: CupsOffer;
  dig: DigOffer;
  whack: WhackOffer;
}

// Phase 2c

/** A friend on a road slot. Restoring a landmark brings its friend along,
 *  and each friend lights one of the four dark lamps with their game. */
export type FriendId =
  | 'hoot' | 'willow' | 'bramble' | 'luna'
  | 'clank' | 'ruby' | 'seal' | 'crown'
  | 'stella' | 'nipper' | 'skipper' | 'pearl'
  | 'mitzi' | 'bjorn' | 'pippin' | 'glimmer'
  | 'digby' | 'shelby' | 'biscuit' | 'goldie'
  | 'presto' | 'sprocket' | 'dazzle' | 'nova'
  | 'humphrey' | 'scout' | 'saffron' | 'sol'
  | 'crumb' | 'ginger' | 'bertie' | 'sundae';

/** The game a friend brings to their lamp. */
export type FriendGame = 'pick' | 'hilo' | 'smash' | 'match' | 'dig' | 'scratch' | 'cups' | 'wheel' | 'whack';

/** A friend's lamp (lamp1 to lamp4) names the friend who lights it and
 *  their game; it is lit once that friend has joined. On a Wheel Run slot a
 *  lamp names only its game, and the slot's stars light it. */
export interface RoadLamp {
  friend?: FriendId;
  game?: FriendGame;
  /** The lamp's free first play is still to come: lit lamps only. */
  freePlay?: boolean;
  /** On a Wheel Run slot, a dark lamp: the stars (1 to 3) that light it. */
  needs?: number;
}

/** A ring lamp names its game too, and its friend on a road. */
export interface BoardRunCell {
  friend?: FriendId;
  game?: FriendGame;
}

/** A STOP on a lit lamp opens that friend's game, which pays into the run
 *  pot; the run waits (no STOP or collect) until it ends. */
export interface BoardStopResult {
  child?: PlayOffer;
}

/** The lamp's game a run is waiting on, so a reload can open it again
 *  (`GET /v1/plays/{playId}`). */
export interface BoardRunChild {
  playId: string;
  kind: PlayKind;
}

export interface BoardRunState {
  child?: BoardRunChild;
}

/** `POST /v1/sessions/{id}/board/first-play`: a lamp's free first game, by
 *  its friend (a road) or by the lamp itself (`lamp1` to `lamp4`, any slot;
 *  a Wheel Run slot's lamps have no friend). With both, the lamp is used. */
export interface FirstPlayRequest {
  themeId: string;
  friend?: FriendId;
  lamp?: RingCellKind;
}

/** A friend's game as its lamp names it. */
export const FRIEND_GAME_LABEL: Record<FriendGame, string> = {
  pick: 'PICK',
  hilo: 'HI-LO',
  smash: 'SMASH',
  match: 'MATCH',
  dig: 'DIG',
  scratch: 'SCRATCH',
  cups: 'CUPS',
  wheel: 'WHEEL',
  whack: 'WHACK',
};

export * from './friends';
export * from './slotGames';

// Phase 3

/** A reel modifier the showrunner can start. */
export type StoryMod = 'streak' | 'bloom' | 'wild' | 'golden';

/** A character who drops by with a game. */
export type VisitorHost = 'lou' | 'willa' | 'carl';

/**
 * Something the showrunner made happen on a spin (`road_story`):
 * - `mod`: a reel modifier for the next `spins` spins;
 * - `box`: a mystery box to open with `playId` (what's inside stays on the
 *   server until it's opened);
 * - `rival`: a gift of `coins` from a rival character (`host`), taken with
 *   `playId`;
 * - `visitor`: a character (`host`) brings a mini-game (`game`) to play now,
 *   taken up with `POST /plays/{playId}/accept` or sent on with `/decline`.
 *   It pays up to `coins` and at least `floor`;
 * - `map`: a piece of a treasure map, `pieces` of `of`. The last piece
 *   completes the map, and its treasure is dug up with `playId`: played as
 *   `game` (the dig, with no bombs), worth `coins` in all, taken up with
 *   `POST /plays/{playId}/accept`. (Without `game`, an older server: the
 *   accept digs it up at once and answers with a `GiftResult`.)
 */
export interface Beat {
  kind: 'mod' | 'box' | 'rival' | 'visitor' | 'map';
  playId?: string;
  mod?: StoryMod;
  spins?: number;
  host?: string;
  coins?: number;
  game?: PlayKind;
  floor?: number;
  pieces?: number;
  of?: number;
}

/** The story on one road slot: modifiers running (spins left each), a gift
 *  or a visitor still waiting, and the treasure map pieces found. */
export interface StoryState {
  mods: Partial<Record<StoryMod, number>>;
  pending?: Beat;
  mapPieces?: number;
  mapOf?: number;
}

/** `POST /v1/plays/{id}/accept` for a gift: what it held, and after. */
export interface GiftResult {
  playId: string;
  kind: 'box' | 'rival' | 'treasure';
  status: 'done';
  prize: { coins: number; gems: number; freeSpins: number };
  wallet: Wallet;
  freeSpinsRemaining: number;
}

/** `POST /v1/plays/{id}/accept` for a visitor: their game, open to play. */
export interface VisitorGame {
  playId: string;
  /** A visitor's game, or the treasure map's dig. */
  kind: 'visitor' | 'treasure';
  status: 'open';
  /** The visitor (none for the treasure). */
  host?: VisitorHost;
  offer: PlayOffer;
}

/** `GET /v1/players/{id}/takings?since=`: what was won since `since` (ms). */
export interface Takings {
  since: number;
  spins: number;
  coinsWon: number;
  /** What the paid spins cost. */
  coinsSpent: number;
  biggestWin: number;
  lampRuns: number;
  gemsWon: number;
  freeSpinsWon: number;
  /** The wallet now. */
  coins: number;
}

/** Side lights a daily-bonus claim lit (`DailyBonusResponse.readyLights`). */
export interface MorningLights {
  themeId: string;
  added: number;
  ready: number;
  readyMax: number;
}

// Reel collectibles (reel_collect: relics on every rr_v1 slot)

/** A cell on the reels (column from the left, row from the top). */
export interface CollectCell {
  col: number;
  row: number;
}

/**
 * A player's relics on one slot (`GET /v1/sessions/{id}/collect`, and
 * `collect` on each spin): which of the set's three they hold (common, rare,
 * legendary), sets completed there, and what the set in progress pays. On a
 * spin it also carries the relic found (`find.piece`, 0 to 2, and where it
 * landed) and, when that completed the set, what the set paid (already in
 * the wallet); `pieces` and `prize` are then the next set's. `find.gold` is
 * true only on the relic the slot gives once for reaching gold (its third
 * star); missing or false on every other find.
 */
export interface CollectState {
  pieces: boolean[];
  sets: number;
  prize: { coins: number; gems: number };
  find?: { piece: number; cells: CollectCell[]; gold?: boolean };
  completed?: { set: number; coins: number; gems: number };
}
